import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { categoryColor, prettyType, CATEGORY_COLORS, CATEGORY_LABELS } from "@/lib/intel";
import { MapPin } from "lucide-react";

const INITIAL_CENTER = [12.9716, 77.5946];

function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

// Raw-Leaflet map. Managed manually (not react-leaflet MapContainer) so it
// survives React 18 StrictMode's mount/unmount/mount cycle: on cleanup we call
// map.remove(), which clears the container's _leaflet_id before the next mount.
function GeoMap({ places }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    if (el._leaflet_id) delete el._leaflet_id; // defensive
    const map = L.map(el, { scrollWheelZoom: true }).setView(INITIAL_CENTER, 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    // Leaflet needs a size recalculation once the flex container has painted.
    setTimeout(() => map.invalidateSize(), 100);
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    const group = L.layerGroup();
    const latlngs = [];
    places.forEach((p) => {
      const color = categoryColor(p.category);
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: Math.min(20, 6 + p.degree * 2),
        color, fillColor: color, fillOpacity: 0.55, weight: 2,
      });
      const clean = p.label.replace("[DEMO] ", "");
      marker.bindTooltip(clean);
      marker.bindPopup(
        `<div style="min-width:140px">
           <div style="font-weight:600;font-size:13px">${esc(clean)}</div>
           <div style="font-size:11px;color:#64748b;font-family:monospace">${esc(prettyType(p.type))} · ${p.degree} links</div>
           ${p.network ? `<div style="font-size:11px;color:#64748b">${esc(p.network)}</div>` : ""}
           <a href="/intel/entities/${encodeURIComponent(p.id)}" style="font-size:12px;color:#1d4ed8">Open profile &rarr;</a>
         </div>`
      );
      marker.on("add", () => {
        const elm = marker.getElement();
        if (elm) elm.setAttribute("data-testid", `geo-marker-${p.id}`);
      });
      group.addLayer(marker);
      latlngs.push([p.lat, p.lng]);
    });
    group.addTo(map);
    layerRef.current = group;
    if (latlngs.length === 1) map.setView(latlngs[0], 13);
    else if (latlngs.length > 1) map.fitBounds(latlngs, { padding: [40, 40] });
  }, [places]);

  return <div ref={containerRef} data-testid="geo-map" style={{ height: "100%", width: "100%" }} />;
}

export default function Geographic() {
  const [places, setPlaces] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [networkFilter, setNetworkFilter] = useState("");
  const [ready, setReady] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [{ data }, { data: stats }] = await Promise.all([
        api.get("/intel/geo", { params: networkFilter ? { network: networkFilter } : {} }),
        api.get("/intel/stats"),
      ]);
      setPlaces(data);
      setNetworks(stats.networks || []);
    } finally {
      setReady(true);
    }
  }, [networkFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const usedCats = useMemo(() => Array.from(new Set(places.map((p) => p.category))), [places]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="geographic-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Intelligence graph</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Geographic intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">Hotspots, hubs, accommodation and recruitment/destination areas. Marker size reflects connectivity.</p>
        </div>
        <select data-testid="geo-network-filter" value={networkFilter} onChange={(e) => setNetworkFilter(e.target.value)} className="border border-slate-300 rounded-md text-sm px-2 py-2">
          <option value="">All networks</option>
          {networks.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </header>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid="geo-legend">
        {usedCats.map((cat) => (
          <span key={cat} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || CATEGORY_COLORS.other }} />
            {CATEGORY_LABELS[cat] || cat}
          </span>
        ))}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden" style={{ height: "calc(100vh - 260px)", minHeight: 420 }}>
        {!ready ? (
          <div className="h-full grid place-items-center text-sm text-slate-500" data-testid="geo-loading">Loading map…</div>
        ) : (
          <GeoMap places={places} />
        )}
      </div>
    </div>
  );
}
